import { useContext, useEffect, useState } from "react"

import { Accordion, AccordionDetails, AccordionSummary, Button, Checkbox, Container, FormControl, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material'
import { useTranslation } from "react-i18next"

import CommonBackButton from '../../../atoms/CommonBackButton/CommonBackButton'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import Spacer from "../../../atoms/Spacer/Spacer"
import { UserContext } from "../../../../stores/User/UserStore"

import { logLandingPageAction } from "../../../../clients/logger"
import useTrackPageView from "../../../../helpers/hooks/use-track-page-view"

import styles from './SubscriptionGiftSenderDetails.module.css'
import SubscriptionGiftStepperStep from "../../../molecules/SubscriptionGiftStepper/SubscriptionGiftStepper"
import CustomInput from "../../../atoms/CustomInput/CustomInput"
import { MembershipStatus } from "../../../../types/MembershipStatus"
import CustomDatePicker from "../../../atoms/CustomDatePicker/CustomDatePicker"
import useGoogleTagManager from "../../../../helpers/analytics/use-google-tag-manager"
import GtmEventName from "../../../../helpers/analytics/GtmEventName"
import { Route } from "../../../../router"
import { Link, useNavigate } from "react-router-dom"
import { useMembershipRepository } from "../../../../repositories/Membership/use-membership-repository"
import SUBSCRIPTION_GIFT_FORM from "../../../../helpers/validation/rules/subscription-gift-form"
import moment from "moment"
import { useSubscriptionGiftRepository } from "../../../../repositories/SubscriptionGift/use-subscription-gift-repository"

const SubscriptionGiftSenderDetails = () => {

    const { t } = useTranslation()
    const { trackButtonClick } = useGoogleTagManager()
    const { retrieveMembership } = useMembershipRepository()
    const { createGiftPaymentOrder } = useSubscriptionGiftRepository()
    const { state: userState } = useContext(UserContext)
    const [nonMemberDeclaration, setNonMemberDeclaration] = useState(false)
    const [validSingpassDeclaration, setValidSingpassDeclaration] = useState(false)
    const [unionMemberDeclaration, setUnionMemberDeclaration] = useState(false)
    const [unionMemberReferralCode, setUnionMemberReferralCode] = useState(``)
    const [validationError, setValidationError] = useState(false)
    const [myName, setMyName] = useState(``)
    const [myEmail, setMyEmail] = useState(``)
    const [senderDateOfBirth, setSenderDateOfBirth] = useState<Date | null>(null)
    const [isUnionActive, setIsUnionActive] = useState(false)
    const validationErrorMessage = `Please double check the form for errors`

    const navigate = useNavigate()

    const membershipStatus = userState.membership?.data?.status
    const userMembership = userState.membership?.data?.type

    const isActiveMember = (userState.membership?.data?.status === MembershipStatus.ACTIVE || userState.membership?.data?.status === MembershipStatus.UNION_ACTIVE)

    useTrackPageView(`Subscription Gift Declaration Page`, `status: ${membershipStatus}, type: ${userMembership} `)

    useEffect(() => {
        if (!userState.membership?.data){
            retrieveMembership()
        }
        if (membershipStatus == MembershipStatus.UNION_ACTIVE){
            setUnionMemberDeclaration(true)
            setIsUnionActive(true)
        }

        if (isActiveMember){
            setMyName(userState.membership?.data?.fullName ?? ``)
            setMyEmail(userState.membership?.data?.emailAddress ?? ``)
            setSenderDateOfBirth(userState.membership?.data?.dateOfBirth || null)
        }
    }, [])

    const areInputsValid = (): boolean => {
        try {
            SUBSCRIPTION_GIFT_FORM.unionMemberReferralCode.parse(unionMemberReferralCode)
            SUBSCRIPTION_GIFT_FORM.myName.parse(myName)
            SUBSCRIPTION_GIFT_FORM.myEmail.parse(myEmail)
            return nonMemberDeclaration
            && validSingpassDeclaration
            && !!myEmail
            && !!myName
            && ((unionMemberDeclaration && unionMemberReferralCode.length > 0) || !unionMemberDeclaration)
        } catch (error) {
            return false
        }
    }

    const handleSubmit = () => {
        if (areInputsValid()){
            setValidationError(false)
            trackButtonClick(GtmEventName.SUBSCRIPTION_GIFT_DECLARATION_SUBMIT)
            logLandingPageAction(`User pressed submit on Submission declaration`, `myInfo`)
            userState.subscriptionGift = {
                ...userState.subscriptionGift,
                unionMemberReferralCode,
                myName,
                myEmail,
                senderDOB: moment(senderDateOfBirth).format(`DDMMYYYY`),
            }
            createGiftPaymentOrder(userState.subscriptionGift)
            navigate(`${Route.SUBSCRIPTION_GIFT_SENDER_PAYMENT}`)
        }else{
            setValidationError(true)
        }
    }

    return (
        <Container>
            <CommonBackButton />
            <Spacer height={24} />
            <SubscriptionGiftStepperStep activeStep={2}/>
            <Spacer height={36} />
            <Typography variant="h3">{t(`subscription_gift_sender_details_form_header`)}</Typography>
            <Spacer height={12} />
            <Typography variant="h4">{t(`subscription_gift_sender_details_form_header_2`)}</Typography>
            <Spacer height={24} />
            <Typography variant="h3">{t(`subscription_gift_sender_details_form_header_3`)}</Typography>
            <Spacer height={24} />
            <CustomInput
                fullWidth
                label={t(`name`)}
                onChange={setMyName}
                readOnly={isActiveMember}
                value={myName}
                validation={SUBSCRIPTION_GIFT_FORM.myName}
                isSignUp={false}
            />
            <CustomInput
                fullWidth
                label={t(`email`)}
                onChange={setMyEmail}
                readOnly={isActiveMember}
                value={myEmail}
                eagerChange
                validation={SUBSCRIPTION_GIFT_FORM.myEmail}
                isSignUp={false}
            />
            {!isActiveMember &&
                <CustomDatePicker
                    fullWidth
                    label={t(`date_of_birth`)}
                    onChange={setSenderDateOfBirth}
                    value={senderDateOfBirth}/>
            }
            {membershipStatus !== MembershipStatus.ACTIVE &&
            <>
                <Spacer height={24} />
                <Typography variant="h3">{t(`declaration_union_member`)}</Typography>
                <Typography variant="h6">
                Find out more about NTUC Union Membership{` `}
                    <Link to={`https://www.ntuc.org.sg/uportal/memberships/ntuc-membership`} target="_blank" rel="noopener noreferrer" style={{ color: `black`, textDecoration: `underline` }}>
                        here
                    </Link>
                    .
                </Typography>
                <FormControl>
                    <Spacer height={24} />
                    <RadioGroup
                        className={styles.radioGroup}
                        row
                        value={unionMemberDeclaration}
                        onChange={() => setUnionMemberDeclaration(!unionMemberDeclaration)}>
                        <FormControlLabel value={true} control={<Radio />} label="Yes" />
                        <FormControlLabel disabled={isUnionActive} value={false} control={<Radio />} label="No" />
                    </RadioGroup>
                </FormControl>
                {unionMemberDeclaration &&
                <>
                    <Spacer height={24} />
                    <CustomInput
                        fullWidth
                        label={t(`declaration_union_member_referral_code_label`)}
                        onChange={setUnionMemberReferralCode}
                        eagerChange
                        placeholder={t(`common_placeholder`)}
                        validation={SUBSCRIPTION_GIFT_FORM.unionMemberReferralCode}
                        value={unionMemberReferralCode}
                        isSignUp={true} />
                    <Accordion elevation={0} disableGutters sx={{'&:before': {display: `none`}}}>
                        <AccordionSummary aria-controls="panel2d-content" id="panel2d-header" expandIcon={<ArrowDropDownIcon />} className={styles.accordionSummary}>
                            <Typography variant="h6" className={styles.accordionText}>{t(`declaration_union_member_referral_code_instructions`)}</Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <img className={styles.accordionImg} src="https://itdstarterappsaccblobsea.blob.core.windows.net/strapi-uploads-prod/assets/Image_Resource_Referral_Sign_up_for_Starter_Gift_Dropdown_83ba84d641.png" alt="Union member referral code instructions" />
                        </AccordionDetails>
                    </Accordion >
                </>
                }
            </>
            }
            <Spacer height={36} />
            <Typography variant="h3">{t(`declaration`)}</Typography>
            <Container className={styles.container}>
                <Checkbox checked={nonMemberDeclaration} onChange={() => setNonMemberDeclaration(!nonMemberDeclaration)} color='functional' />
                <Typography variant="h6">
                    {t(`declaration_non_member`)}
                </Typography>
            </Container>
            <Spacer height={12} />
            <Container className={styles.container}>
                <Checkbox checked={validSingpassDeclaration} onChange={() => setValidSingpassDeclaration(!validSingpassDeclaration)} color='functional' />
                <Typography variant="h6">
                    {t(`declaration_valid_singpass`)}
                </Typography>
            </Container>
            {validationError && <Typography className={styles.validationError}>{validationErrorMessage}</Typography>}
            <Spacer height={24} />
            <Button variant='contained' onClick={handleSubmit} fullWidth>{t(`subscription_gift_review_and_pay`)}</Button>
        </Container>
    )
}

export default SubscriptionGiftSenderDetails